import { useState } from "react";
import { api } from "../api";

const fmt = (v) =>
  "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function StateTable({ data, start, end }) {
  const [expanded, setExpanded] = useState(null);
  const [stateData, setStateData] = useState({});
  const [loading, setLoading] = useState(null);
  const [view, setView] = useState("selling"); // 'selling' | 'not_selling'

  if (!data || data.length === 0)
    return <div className="empty-state">No state data for this period</div>;

  const maxRev = Math.max(...data.map((s) => s.revenue));

  const toggle = async (stateName) => {
    if (expanded === stateName) {
      setExpanded(null);
      return;
    }
    setExpanded(stateName);
    if (!stateData[stateName]) {
      setLoading(stateName);
      try {
        const res = await api.stateProducts(start, end, stateName);
        setStateData((prev) => ({ ...prev, [stateName]: res }));
      } catch {
        setStateData((prev) => ({ ...prev, [stateName]: null }));
      } finally {
        setLoading(null);
      }
    }
  };

  const toggle_new = async (stateName) => {
    console.log("StateName = ", stateName);
  };

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div className="card-title" style={{ margin: 0 }}>
          Revenue by state
        </div>

        {/* View toggle */}
        <div style={{ display: "flex", gap: "6px" }}>
          {[
            { key: "selling", label: "✅ Selling", color: "var(--green)" },
            { key: "not_selling", label: "❌ Not Selling", color: "var(--crimson)" },
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              style={{
                padding: "5px 14px",
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
                background: view === v.key ? v.color : "var(--surface-2)",
                color: view === v.key ? "#fff" : "var(--muted)",
                border: `1px solid ${view === v.key ? v.color : "var(--border)"}`,
                transition: "all .15s",
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {data.map((s, i) => {
          const isOpen = expanded === s.state;
          const isLoading = loading === s.state;
          const info = stateData[s.state];

          const showList = info
            ? view === "selling"
              ? info.sold_products
              : info.unsold_products
            : [];

          return (
            <div key={s.state}>
              {/* ── State row ────────────────────── */}
              <div
                onClick={() => toggle(s.state)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "32px 1fr 110px 60px 70px 130px",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  background: isOpen ? "var(--surface-hover)" : "transparent",
                  border: `1px solid ${isOpen ? "var(--accent-border)" : "transparent"}`,
                  transition: "all .15s",
                }}
              >
                {/* Rank */}
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--light)",
                    textAlign: "center",
                  }}
                >
                  {i + 1}
                </div>

                {/* State name + arrow */}
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      color: isOpen ? "var(--green)" : "var(--muted)",
                      transition: "transform .2s",
                      display: "inline-block",
                      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                      flexShrink: 0,
                    }}
                  >
                    ▶
                  </span>
                  <span style={{ fontWeight: 500, fontSize: "13px" }}>
                    {s.state}
                  </span>
                </div>

                {/* Revenue */}
                <div
                  style={{
                    fontWeight: 700,
                    color: "var(--green)",
                    whiteSpace: "nowrap",
                    fontSize: "13px",
                  }}
                >
                  {fmt(s.revenue)}
                </div>

                {/* Bills */}
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "var(--muted)",
                  }}
                >
                  {s.total_bills}
                </div>

                {/* Customers */}
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "var(--muted)",
                  }}
                >
                  {s.unique_customers}
                </div>

                {/* Share bar + badges */}
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      flex: 1,
                      height: "6px",
                      borderRadius: "3px",
                      background: "var(--border)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        borderRadius: "3px",
                        background: "var(--green)",
                        width: `${(s.revenue / maxRev) * 100}%`,
                      }}
                    />
                  </div>
                  {/* Selling / not-selling badges — show when loaded */}
                  {info && (
                    <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                      <span
                        style={{
                          fontSize: "10px",
                          background: "var(--green-light)",
                          color: "var(--green-dark)",
                          padding: "1px 5px",
                          borderRadius: "999px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ✅ {info.selling}
                      </span>
                      <span
                        style={{
                          fontSize: "10px",
                          background: "var(--red-light)",
                          color: "var(--red-dark)",
                          padding: "1px 5px",
                          borderRadius: "999px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ❌ {info.not_selling}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Expanded panel ───────────────── */}
              {isOpen && (
                <div
                  style={{
                    margin: "0 0 4px 44px",
                    background: "var(--surface-tint)",
                    border: "1px solid var(--accent-border)",
                    borderTop: `2px solid ${view === "selling" ? "var(--green)" : "var(--crimson)"}`,
                    borderRadius: "0 0 10px 10px",
                    padding: "14px 16px",
                    animation: "slideDown .2s ease",
                  }}
                >
                  {isLoading ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "16px",
                        color: "var(--muted)",
                        fontSize: "13px",
                      }}
                    >
                      <span className="spinner" /> Loading product breakdown…
                    </div>
                  ) : !info ? (
                    <div style={{ color: "var(--muted)", fontSize: "13px" }}>
                      Failed to load
                    </div>
                  ) : (
                    <>
                      {/* Coverage bar */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginBottom: "14px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            height: "10px",
                            background: "var(--border)",
                            borderRadius: "5px",
                            overflow: "hidden",
                            minWidth: "120px",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              borderRadius: "5px",
                              background: "var(--green)",
                              width: `${(info.selling / info.total_products) * 100}%`,
                              transition: "width .5s ease",
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "var(--green-dark)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {info.selling} / {info.total_products} products
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            color: "var(--muted)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          (
                          {((info.selling / info.total_products) * 100).toFixed(
                            0,
                          )}
                          % coverage)
                        </span>
                      </div>

                      {/* Section title */}
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          marginBottom: "10px",
                          color: view === "selling" ? "var(--green-dark)" : "var(--red-dark)",
                        }}
                      >
                        {view === "selling"
                          ? `✅ ${info.selling} products selling in ${s.state}`
                          : `❌ ${info.not_selling} products not selling in ${s.state}`}
                      </div>

                      {/* ── Selling products — with revenue/qty/orders ── */}
                      {view === "selling" &&
                        (showList.length === 0 ? (
                          <div
                            style={{ fontSize: "12px", color: "var(--muted)" }}
                          >
                            No products found
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}
                          >
                            {showList.map((p, pi) => {
                              const maxPRev = showList[0]?.revenue || 1;
                              return (
                                <div
                                  key={p.product_name}
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "24px 1fr 100px 55px 60px",
                                    alignItems: "center",
                                    gap: "10px",
                                    fontSize: "12px",
                                    padding: "5px 8px",
                                    borderRadius: "6px",
                                    background:
                                      pi === 0 ? "var(--surface-hover)" : "transparent",
                                  }}
                                >
                                  {/* Rank */}
                                  <span
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      flexShrink: 0,
                                      background:
                                        pi < 3
                                          ? ["var(--green)", "var(--blue)", "var(--amber)"][
                                              pi
                                            ]
                                          : "var(--border)",
                                      color: pi < 3 ? "#fff" : "var(--muted)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "9px",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {pi + 1}
                                  </span>

                                  {/* Name + bar */}
                                  <div>
                                    <div
                                      style={{
                                        fontWeight: 500,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        marginBottom: "3px",
                                      }}
                                    >
                                      {p.product_name}
                                    </div>
                                    <div
                                      style={{
                                        height: "4px",
                                        background: "var(--border)",
                                        borderRadius: "2px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          height: "100%",
                                          borderRadius: "2px",
                                          background: "var(--green)",
                                          width: `${(p.revenue / maxPRev) * 100}%`,
                                        }}
                                      />
                                    </div>
                                  </div>

                                  {/* Revenue */}
                                  <div
                                    style={{
                                      textAlign: "right",
                                      fontWeight: 700,
                                      color: "var(--green)",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {fmt(p.revenue)}
                                  </div>

                                  {/* Qty */}
                                  <div
                                    style={{
                                      textAlign: "center",
                                      color: "var(--muted)",
                                    }}
                                  >
                                    {p.qty} qty
                                  </div>

                                  {/* Orders */}
                                  <div style={{ textAlign: "center" }}>
                                    <span
                                      style={{
                                        background: "var(--green-light)",
                                        color: "var(--green-dark)",
                                        padding: "1px 6px",
                                        borderRadius: "999px",
                                        fontSize: "10px",
                                      }}
                                    >
                                      {p.orders} orders
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}

                      {/* ── Not selling products — simple grid ── */}
                      {view === "not_selling" &&
                        (showList.length === 0 ? (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "var(--green-dark)",
                              fontWeight: 500,
                            }}
                          >
                            🎉 All products are selling in {s.state}!
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(180px, 1fr))",
                              gap: "6px",
                            }}
                          >
                            {showList.map((productName) => (
                              <div
                                key={productName}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "5px 10px",
                                  borderRadius: "6px",
                                  background: "var(--red-light)",
                                  border: "1px solid var(--red-border)",
                                  fontSize: "11px",
                                  color: "var(--red-dark)",
                                }}
                              >
                                <span style={{ flexShrink: 0 }}>✕</span>
                                <span
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontWeight: 500,
                                  }}
                                >
                                  {productName}
                                </span>
                              </div>
                            ))}
                          </div>
                        ))}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
